import React, { useEffect, useState, useMemo } from "react";
import withRouter from "components/Common/withRouter";
import TableContainer from "../../../components/Common/TableContainer";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  Label,
  FormFeedback,
  Input,
  Form,
  Badge,
  Spinner
} from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import axios from 'axios';
import toastr from 'toastr';
import moment from "moment";
import Breadcrumbs from "components/Common/Breadcrumb";
import DeleteModal from "components/Common/DeleteModal";
import "toastr/build/toastr.min.css";
import firebase from "firebase/compat/app";
// CKEditor
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { CKEditor } from '@ckeditor/ckeditor5-react';

// FIREBASE

import { getStorage, ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";

const ResourcesList = () => {
  document.title = "Kaynak & Blog Yönetimi | Subsea Admin";

  // --- API Yapılandırması ---
  // Controller adını backend'e göre güncellemelisin (Örn: /api/Resources)
  const API_URL = "https://subseaapi.stechomeyazilim.info/Resources";
  
  // --- State Tanımları ---
  const [resources, setResources] = useState([]);
  const [resourceEdit, setResourceEdit] = useState(null);
  
  // Resim Yükleme (Firebase)
  const [uploadedImage, setUploadedImage] = useState(null);
  const [uploading, setUploading] = useState(false);

  // CKEditor İçerik
  const [editorData, setEditorData] = useState("");

  // Modallar
  const [modal, setModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [resourceToDelete, setResourceToDelete] = useState(null);

  // --- Verileri Çekme ---
  useEffect(() => {
    fetchResources();
  }, []);

  const fetchResources = async () => {
    try {
      const response = await axios.get(API_URL);
      const data = Array.isArray(response.data.value) ? response.data.value : response.data;
      setResources(data);
    } catch (error) {
      console.error("Resources fetch error:", error);
      toastr.error("Kaynaklar yüklenemedi.");
    }
  };

  
  const handleImageUpload = async (file) => {
    if (!file) return;
    try {
      setUploading(true);
      
      // ÖNEMLİ DÜZELTME:
      // Helper dosyanız 'compat' kullandığı için, başlatılmış app örneğini buraya veriyoruz.
      const storage = getStorage(firebase.app()); 
      
      const fileName = `resources/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, fileName);
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on("state_changed", 
        (snapshot) => {
            // İsteğe bağlı progress logu
            // const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            // console.log('Upload is ' + progress + '% done');
        }, 
        (err) => {
          console.error("Firebase Hatası:", err); // Hatayı konsolda detaylı gör
          setUploading(false);
          toastr.error("Resim yüklenemedi: " + err.message);
        }, 
        () => {
          getDownloadURL(uploadTask.snapshot.ref).then((url) => {
            console.log("Yüklenen URL:", url);
            setUploadedImage(url);
            setUploading(false);
            toastr.success("Görsel başarıyla yüklendi.");
          });
      });
    } catch (error) {
      console.error("Genel Hata:", error);
      setUploading(false);
      toastr.error("Yükleme sırasında hata oluştu.");
    }
  };

  // --- Formik Yapılandırması ---
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      Title: resourceEdit?.Title || "",
      Slug: resourceEdit?.Slug || "",
      Summary: resourceEdit?.Summary || "",
      Category: resourceEdit?.Category || "General",
      Author: resourceEdit?.Author || "SubseaBay Team",
      IsActive: resourceEdit?.IsActive ?? true,
    },
    validationSchema: Yup.object({
      Title: Yup.string().required("Başlık zorunludur"),
      Slug: Yup.string().required("Slug zorunludur"),
      Category: Yup.string().required("Kategori zorunludur"),
    }),
    onSubmit: async (values) => {
      const payload = {
        ...values,
        Content: editorData, // CKEditor'den gelen veri
        ImageUrl: uploadedImage || (resourceEdit ? resourceEdit.ImageUrl : ""),
        PublishedAt: isEdit ? resourceEdit.PublishedAt : new Date().toISOString()
      };

      try {
        if (isEdit && resourceEdit) {
            await axios.put(`${API_URL}(${resourceEdit.ID})`, { ID: resourceEdit.ID, ...payload });
            toastr.success("Kaynak güncellendi.");
        } else {
            await axios.post(API_URL, payload);
            toastr.success("Kaynak eklendi.");
        }
        setModal(false);
        fetchResources();
      } catch (error) {
        console.error("Save error:", error);
        toastr.error("Kaydetme sırasında hata oluştu (Slug benzersiz olmalı).");
      }
    },
  });

  // --- Slug Otomatik Oluşturucu ---
  const handleTitleChange = (e) => {
    validation.handleChange(e);
    if (!isEdit) {
        const slug = e.target.value
            .toLowerCase()
            .trim()
            .replace(/ /g, '-')
            .replace(/[^\w-]+/g, '');
        validation.setFieldValue('Slug', slug);
    }
  };

  // --- Tablo Kolonları ---
  const columns = useMemo(() => [
      {
        Header: "#",
        accessor: "ID",
        width: 50,
      },
      {
        Header: "Görsel",
        accessor: "ImageUrl",
        disableFilters: true,
        Cell: (cellProps) => (
          <div className="avatar-xs">
            {cellProps.value ? (
              <img src={cellProps.value} alt="Img" className="rounded h-100 object-cover w-100" />
            ) : (
              <div className="avatar-title rounded bg-soft-primary text-primary">R</div>
            )}
          </div>
        ),
      },
      {
        Header: "Başlık",
        accessor: "Title",
        filterable: true,
        Cell: (cellProps) => (
            <div>
                <h5 className="font-size-14 mb-1 text-truncate" style={{maxWidth: '200px'}}>{cellProps.value}</h5>
                <small className="text-muted">/{cellProps.row.original.Slug}</small>
            </div>
        )
      },
      { Header: "Kategori", accessor: "Category", filterable: true },
      { Header: "Yazar", accessor: "Author", filterable: true },
      {
        Header: "Yayın Tarihi",
        accessor: "PublishedAt",
        Cell: (cellProps) => moment(cellProps.value).format("DD/MM/YYYY")
      },
      {
        Header: "Durum",
        accessor: "IsActive",
        Cell: (cellProps) => (
            <Badge color={cellProps.value ? "success" : "secondary"}>
                {cellProps.value ? "Aktif" : "Pasif"}
            </Badge>
        )
      },
      {
        Header: "İşlemler",
        Cell: (cellProps) => (
            <div className="d-flex gap-2">
              <button className="btn btn-sm btn-soft-info" onClick={() => handleEditClick(cellProps.row.original)}>
                <i className="mdi mdi-pencil" />
              </button>
              <button className="btn btn-sm btn-soft-danger" onClick={() => handleDeleteClick(cellProps.row.original)}>
                <i className="mdi mdi-delete" />
              </button>
            </div>
        ),
      },
    ], []
  );

  // --- Modal İşlemleri ---
  const toggleModal = () => {
    setModal(!modal);
    if (!modal && !isEdit) {
        validation.resetForm();
        setEditorData("");
        setUploadedImage(null);
    }
  };

  const handleEditClick = (data) => {
    setResourceEdit(data);
    setIsEdit(true);
    setEditorData(data.Content || "");
    setUploadedImage(data.ImageUrl);
    setModal(true);
  };

  const handleAddClick = () => {
    setResourceEdit(null);
    setIsEdit(false);
    setEditorData("");
    setUploadedImage(null);
    setModal(true);
  };

  const handleDeleteClick = (data) => {
    setResourceToDelete(data);
    setDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (resourceToDelete) {
        try {
            await axios.delete(`${API_URL}(${resourceToDelete.ID})`);
            toastr.success("Kayıt silindi.");
            fetchResources();
        } catch (error) { toastr.error("Silme hatası."); }
    }
    setDeleteModal(false);
  };

  return (
    <React.Fragment>
      <DeleteModal show={deleteModal} onDeleteClick={confirmDelete} onCloseClick={() => setDeleteModal(false)} />
      
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Yönetim" breadcrumbItem="Kaynaklar (Resources)" />

          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <TableContainer
                    columns={columns}
                    data={resources}
                    isGlobalFilter={true}
                    isAddBlog={true} // + Butonu için
                    handleOrderClicks={handleAddClick}
                    customPageSize={10}
                    className="custom-header-css"
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Modal isOpen={modal} toggle={toggleModal} size="xl">
            <ModalHeader toggle={toggleModal} tag="h4">
              {isEdit ? "Kaynağı Düzenle" : "Yeni Kaynak Ekle"}
            </ModalHeader>
            <ModalBody>
              <Form onSubmit={(e) => { e.preventDefault(); validation.handleSubmit(); }}>
                <Row>
                    <Col md={6} className="mb-3">
                        <Label>Başlık (Title) *</Label>
                        <Input 
                            name="Title" 
                            type="text" 
                            onChange={handleTitleChange} 
                            onBlur={validation.handleBlur} 
                            value={validation.values.Title} 
                            invalid={!!(validation.touched.Title && validation.errors.Title)} 
                        />
                        <FormFeedback>{validation.errors.Title}</FormFeedback>
                    </Col>
                    
                    <Col md={6} className="mb-3">
                        <Label>Slug (URL) *</Label>
                        <Input 
                            name="Slug" 
                            type="text" 
                            onChange={validation.handleChange} 
                            onBlur={validation.handleBlur} 
                            value={validation.values.Slug} 
                            invalid={!!(validation.touched.Slug && validation.errors.Slug)} 
                        />
                        <FormFeedback>{validation.errors.Slug}</FormFeedback>
                    </Col>

                    <Col md={6} className="mb-3">
                        <Label>Kategori *</Label>
                        <Input type="select" name="Category" value={validation.values.Category} onChange={validation.handleChange}>
                            <option value="General">General</option>
                            <option value="News">News</option>
                            <option value="Case Study">Case Study</option>
                            <option value="Whitepaper">Whitepaper</option>
                            <option value="Guide">Guide</option>
                        </Input>
                    </Col>

                    <Col md={6} className="mb-3">
                        <Label>Yazar</Label>
                        <Input name="Author" type="text" onChange={validation.handleChange} value={validation.values.Author} />
                    </Col>

                    <Col md={12} className="mb-3">
                        <Label>Özet (Summary)</Label>
                        <Input type="textarea" rows="2" name="Summary" onChange={validation.handleChange} value={validation.values.Summary} />
                    </Col>

                    <Col md={12} className="mb-3">
                        <Label>İçerik (Content)</Label>
                        <CKEditor
                            editor={Editor}
                            data={editorData}
                            onChange={(event, editor) => {
                                setEditorData(editor.getData());
                            }}
                        />
                    </Col>

                    <Col md={12} className="mb-3 p-3 bg-light rounded border">
                        <Label>Kapak Görseli (Firebase)</Label>
                        <div className="d-flex align-items-center gap-3">
                            <div className="avatar-lg">
                                {uploadedImage ? (
                                    <img src={uploadedImage} alt="Preview" className="img-thumbnail rounded h-100 object-cover" />
                                ) : (
                                    <div className="avatar-title rounded bg-soft-secondary text-secondary">
                                        <i className="mdi mdi-image-plus font-size-24"></i>
                                    </div>
                                )}
                            </div>
                            <div className="flex-grow-1">
                                <Input type="file" accept="image/*" onChange={(e) => { if(e.target.files[0]) handleImageUpload(e.target.files[0]); }} />
                                {uploading && <div className="mt-2 text-primary"><Spinner size="sm"/> Yükleniyor...</div>}
                            </div>
                        </div>
                    </Col>
                    
                    <Col md={12}>
                        <div className="form-check form-switch">
                            <Input 
                                type="checkbox" 
                                className="form-check-input" 
                                id="IsActive" 
                                name="IsActive" 
                                checked={validation.values.IsActive} 
                                onChange={validation.handleChange} 
                            />
                            <Label className="form-check-label" htmlFor="IsActive">Yayında (Active)</Label>
                        </div>
                    </Col>
                </Row>
                
                <div className="text-end mt-3">
                    <button type="button" className="btn btn-secondary me-2" onClick={toggleModal}>İptal</button>
                    <button type="submit" className="btn btn-success" disabled={uploading}>{isEdit ? "Güncelle" : "Kaydet"}</button>
                </div>
              </Form>
            </ModalBody>
          </Modal>

        </Container>
      </div>
    </React.Fragment>
  );
};

export default withRouter(ResourcesList);